import { useEffect, useState } from "react";
import { cn } from "@edusogno/lib/utils";
import { useMe } from "@/data/user.ts";
import { useNavigate } from "react-router-dom";
import { BookExamsToDo } from "@/types";
import { useBreakpoint } from "@/utils/hooks/useBreakpoints.ts";
import { useTranslation } from "react-i18next";
import { useUploadPicBookExamMutation } from "@/data/ielts";
import dayjs from "dayjs";
import routes from "@/config/routes.ts";
import useGlobals from "@/utils/hooks/useGlobals.ts";
import MobileView from "@/components/book-exam/MobileView.tsx";
import SelectIcon from "@/components/icons/book-exam/SelectIcon";
import PageHeading from "@/components/ui/PageHeading.tsx";
import DesktopView from "@/components/book-exam/DesktopView.tsx";
import LocationIcon from "@/components/icons/book-exam/LocationIcon";
import CreateAccountIcon from "@/components/icons/book-exam/CreateAccountIcon";
import DashboardContentLayout from "@/layouts/DashboardContentLayout.tsx";

export const LINK_WHATSAPP_HELP =
  "https://api.whatsapp.com/send?phone=393755858639";

export default function BookExam() {
  const { me } = useMe();
  const user = me?.item;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const breakpoint = useBreakpoint();
  const isSmallScreen = ["xs", "sm", "md"].indexOf(breakpoint) !== -1;
  const { setGlobalState } = useGlobals();

  const { mutate, isLoading } = useUploadPicBookExamMutation();

  const [photo, setPhoto] = useState<FileList | null>(null);
  const [completed, setCompleted] = useState<number[]>([]);
  const [currentStep, setCurrentStep] = useState<number>(0);

  const toDo: BookExamsToDo[] = [
    {
      icon: SelectIcon,
      text: t("book_exam.todo.one"),
    },
    {
      icon: LocationIcon,
      text: t("book_exam.todo.two"),
    },
    {
      icon: CreateAccountIcon,
      text: t("book_exam.todo.three"),
    },
  ];

  const steps: string[] = [
    t("book_exam.steps.one.step"),
    // t("book_exam.steps.two.step"),
    t("book_exam.steps.three.step"),
  ];

  let kickoffDateObj: Date | dayjs.Dayjs = new Date();
  if (user?.date_active_book_exam) {
    kickoffDateObj = new Date(user.date_active_book_exam);
  } else if (user?.end_kickoff_date) {
    kickoffDateObj = dayjs(user.end_kickoff_date);
    kickoffDateObj = kickoffDateObj.subtract(40, "days");
    kickoffDateObj = new Date(kickoffDateObj.format("YYYY-MM-DD"));
  }

  const uploadPicBookExam = () => {
    if (!me || !photo) return;
    mutate({ user_id: me.item.user.id, booking_pic: photo[0] });
    setPhoto(null);
  };

  useEffect(() => {
    setGlobalState("backgroundClass", "bg-brand-background");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !me?.item?.hasIelts ||
      ["es", "de"].includes(me?.item?.user?.lang ?? "")
    ) {
      navigate(routes.agenda);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.item]);

  useEffect(() => {
    if (!me?.item.user || !me?.item.user?.products?.length) {
      return;
    }

    const checkUserBookingByMonth =
      me.item.user?.check_user_booking_ielts ?? [];

    const lastCheckUserBookingByMonth =
      checkUserBookingByMonth?.at(-1)?.created_at;

    const lastProduct = me.item.user.products?.at(-1)?.pivot;

    const now = new Date();

    if (
      lastCheckUserBookingByMonth &&
      lastProduct &&
      lastProduct?.start_date &&
      lastProduct?.end_date
    ) {
      const start = new Date(lastProduct?.start_date);
      const end = new Date(lastProduct?.end_date);

      const isCompleted =
        // deadline is not past
        now < kickoffDateObj &&
        // check user booking by month is before deadline
        new Date(lastCheckUserBookingByMonth) < kickoffDateObj &&
        // i'm into product range
        start < now &&
        now < end &&
        // check user booking by month is into product range
        start < new Date(lastCheckUserBookingByMonth) &&
        new Date(lastCheckUserBookingByMonth) < end;

      setCurrentStep(isCompleted ? 2 : 0);
      setCompleted(isCompleted ? [2] : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.item]);

  const isPastKickObj = kickoffDateObj <= new Date();

  return (
    <DashboardContentLayout className="overflow-y-auto">
      <PageHeading
        title={t("menu.book_exam")}
        button={
          <div className="w-full flex flex-col justify-start gap-1">
            <div
              className={cn(
                "rounded-md px-4 lg:px-6 py-2 mt-2 lg:mt-0 w-full  flex justify-center items-center",
                isPastKickObj ? "bg-[#E31837]" : "bg-shade-five",
              )}
            >
              <p
                className={cn(
                  "text-14px font-medium font-[Inter]",
                  isPastKickObj ? "text-white" : "text-[--secondary]",
                )}
              >
                {isPastKickObj
                  ? t("book_exam.booking_deadline_is_past")
                  : t("book_exam.booking_deadline")}
                :&nbsp;
                <b>
                  {kickoffDateObj.getDate()}{" "}
                  {t(`months.short.${kickoffDateObj.getMonth() + 1}`)}
                </b>
              </p>
            </div>
            <p className="text-14px until-lg:mb-4 font-medium font-[Inter] text-[#E31837]">
              {isPastKickObj &&
                t("book_exam.you_have_exceeded_your_booking_deadline")}
            </p>
          </div>
        }
        subtitle={t("book_exam.sub_header")}
        className="md:flex-col lg:flex-row md:gap-4 lg:gap-0 md:pb-0 md:pt-0 lg:pb-10"
        subtitleClassName="text-light"
      />

      <div className="flex flex-col justify-center grow mb-4">
        {isSmallScreen ? (
          <MobileView
            toDo={toDo}
            steps={steps}
            photo={photo}
            setPhoto={setPhoto}
            deadline={kickoffDateObj}
            onSubmit={uploadPicBookExam}
            isLoading={isLoading}
            completed={completed}
            currentStep={currentStep}
            setCompleted={setCompleted}
            isPastKickObj={isPastKickObj}
            setCurrentStep={setCurrentStep}
          />
        ) : (
          <DesktopView
            toDo={toDo}
            steps={steps}
            photo={photo}
            setPhoto={setPhoto}
            deadline={kickoffDateObj}
            onSubmit={uploadPicBookExam}
            completed={completed}
            isLoading={isLoading}
            currentStep={currentStep}
            setCompleted={setCompleted}
            isPastKickObj={isPastKickObj}
            setCurrentStep={setCurrentStep}
          />
        )}
      </div>
    </DashboardContentLayout>
  );
}
